/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { fetchAccounts, fetchAccountDetails } from '../../api/api';
import { useTrade } from '../../context/TradeProvider';
import API_BASE_URL, { WS_BASE_URL } from '../../api/config';

/**
 * AccountBalanceWidget - Compact widget showing account balance, equity, and P/L
 * Supports live updates via WebSocket
 */
export default function AccountBalanceWidget({ accountId: propAccountId, restrictToAccount = false }) {
  const { accountId: contextAccountId } = useTrade();
  
  // data
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [accountData, setAccountData] = useState({
    balance: null,
    equity: null,
    profit: null,
    margin: null,
    freeMargin: null,
    marginLevel: null,
    currency: 'USD',
  });
  
  // ui state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  
  // WebSocket ref
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Load accounts list
  const loadAccounts = useCallback(async () => {
    try {
      const resp = await fetchAccounts();
      const arr = Array.isArray(resp) ? resp : (resp?.results || []);
      setAccounts(arr);
      
      // Set initial selected account
      const targetId = propAccountId || contextAccountId;
      if (targetId && arr.some(a => a.id === targetId)) {
        setSelectedAccountId(targetId);
      } else if (arr.length > 0) {
        setSelectedAccountId(arr[0].id);
      }
    } catch (e) {
      console.error('AccountBalanceWidget: fetchAccounts failed', e);
      setError('Failed to load accounts');
    }
  }, [propAccountId, contextAccountId]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Sync with context account when it changes
  useEffect(() => {
    if (!restrictToAccount && contextAccountId && accounts.some(a => a.id === contextAccountId)) {
      setSelectedAccountId(contextAccountId);
    }
  }, [contextAccountId, accounts, restrictToAccount]);

  // Fetch initial account data and connect WebSocket
  useEffect(() => {
    if (!selectedAccountId) return;

    let alive = true;
    setLoading(true);
    setError(null);

    // Fetch initial snapshot
    const fetchInitial = async () => {
      try {
        const details = await fetchAccountDetails(selectedAccountId);
        if (!alive) return;
        
        setAccountData({
          balance: details.balance ?? null,
          equity: details.equity ?? null,
          profit: details.profit ?? details.unrealized_pnl ?? null,
          margin: details.margin ?? details.used_margin ?? null,
          freeMargin: details.free_margin ?? null,
          marginLevel: details.margin_level ?? null,
          currency: details.currency || 'USD',
        });
      } catch (e) {
        if (!alive) return;
        console.error('AccountBalanceWidget: fetchAccountDetails failed', e);
        // Try to get data from accounts list as fallback
        const acc = accounts.find(a => a.id === selectedAccountId);
        if (acc) {
          setAccountData({
            balance: acc.balance ?? null,
            equity: acc.equity ?? null,
            profit: acc.profit ?? null,
            margin: acc.margin ?? acc.used_margin ?? null,
            freeMargin: acc.free_margin ?? null,
            marginLevel: acc.margin_level ?? null,
            currency: acc.currency || 'USD',
          });
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchInitial();

    // Connect WebSocket for live updates
    const connectWs = () => {
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        console.warn('AccountBalanceWidget: No auth token for WebSocket');
        return;
      }

      let origin = '';
      if (WS_BASE_URL && typeof WS_BASE_URL === 'string' && WS_BASE_URL.length > 0) {
        origin = WS_BASE_URL.replace(/\/$/, '');
      } else if (API_BASE_URL) {
        try {
          const tmp = new URL(API_BASE_URL);
          const proto = tmp.protocol === 'https:' ? 'wss' : 'ws';
          origin = `${proto}://${tmp.host}`;
        } catch (err) { /* ignore */ }
      }
      if (!origin && typeof window !== 'undefined') {
        origin = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;
      }

      try {
        const ws = new WebSocket(`${origin}/ws/accounts/${selectedAccountId}/?token=${authToken}`);
        
        ws.onopen = () => {
          if (!alive) { ws.close(); return; }
          setIsConnected(true);
        };

        ws.onclose = () => {
          if (!alive) return;
          setIsConnected(false);
          // Attempt reconnect after 5 seconds
          reconnectTimeoutRef.current = setTimeout(() => {
            if (alive) connectWs();
          }, 5000);
        };

        ws.onerror = (evt) => {
          console.warn('AccountBalanceWidget WS error', evt);
        };

        ws.onmessage = (event) => {
          if (!alive) return;
          try {
            const message = JSON.parse(event.data);
            if (message.type === 'account_update' && message.data) {
              const d = message.data;
              setAccountData(prev => ({
                ...prev,
                balance: d.balance ?? prev.balance,
                equity: d.equity ?? prev.equity,
                profit: d.profit ?? d.unrealized_pnl ?? prev.profit,
                margin: d.margin ?? d.used_margin ?? prev.margin,
                freeMargin: d.free_margin ?? prev.freeMargin,
                marginLevel: d.margin_level ?? prev.marginLevel,
              }));
            }
          } catch (err) { /* ignore parse errors */ }
        };

        wsRef.current = ws;
      } catch (err) {
        console.warn('AccountBalanceWidget WS connect failed', err);
      }
    };

    connectWs();

    return () => {
      alive = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
        wsRef.current = null;
      }
    };
  }, [selectedAccountId, accounts]);

  // Format currency
  const formatCurrency = (value, currency = 'USD') => {
    if (value == null) return '—';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return `$${Number(value).toFixed(2)}`;
    }
  };

  // Format percentage
  const formatPercent = (value) => {
    if (value == null) return '—';
    return `${Number(value).toFixed(2)}%`;
  };

  // Calculate P/L percentage
  const plPercent = accountData.balance && accountData.profit 
    ? (accountData.profit / accountData.balance) * 100 
    : null;

  const selectedAccount = accounts.find(a => a.id === selectedAccountId);
  const currency = accountData.currency || selectedAccount?.currency || 'USD';

  return (
    <Card className="h-full bg-transparent border-0">
      {/* Header with account selector */}
      <div className="px-2 pt-1 pb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {!restrictToAccount && accounts.length > 1 && (
            <select
              className="text-xs bg-[rgb(28,33,37)] border border-white/10 rounded px-2 py-1 text-white/90 max-w-[140px] truncate"
              value={selectedAccountId || ''}
              onChange={(e) => setSelectedAccountId(e.target.value)}
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          )}
          {(restrictToAccount || accounts.length <= 1) && selectedAccount && (
            <span className="text-xs text-white/70 truncate">{selectedAccount.name}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-400' : 'bg-gray-500'}`} title={isConnected ? 'Live' : 'Disconnected'} />
          <span className="text-[10px] text-white/50">{isConnected ? 'Live' : 'Offline'}</span>
        </div>
      </div>

      <CardContent className="px-2 py-2 space-y-2">
        {error && <div className="text-xs text-rose-400">{error}</div>}
        
        {loading && !accountData.balance && (
          <div className="text-xs text-gray-400">Loading…</div>
        )}

        {/* Main metrics */}
        {(accountData.balance != null || accountData.equity != null) && (
          <div className="space-y-2">
            {/* Balance & Equity row */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white/5 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2">
                <div className="text-[10px] sm:text-xs text-white/60">Balance</div>
                <div className="text-sm sm:text-base font-semibold text-white/90 truncate">
                  {formatCurrency(accountData.balance, currency)}
                </div>
              </div>
              <div className="bg-white/5 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2">
                <div className="text-[10px] sm:text-xs text-white/60">Equity</div>
                <div className="text-sm sm:text-base font-semibold text-white/90 truncate">
                  {formatCurrency(accountData.equity, currency)}
                </div>
              </div>
            </div>

            {/* P/L row */}
            <div className="bg-white/5 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] sm:text-xs text-white/60">Unrealized P/L</div>
                  <div className={`text-sm sm:text-lg font-semibold ${
                    accountData.profit == null ? 'text-white/90' :
                    accountData.profit >= 0 ? 'text-emerald-400' : 'text-rose-400'
                  }`}>
                    {accountData.profit != null && accountData.profit >= 0 ? '+' : ''}
                    {formatCurrency(accountData.profit, currency)}
                  </div>
                </div>
                {plPercent != null && (
                  <div className={`text-xs sm:text-sm font-medium px-2 py-0.5 rounded ${
                    plPercent >= 0 ? 'bg-emerald-400/20 text-emerald-400' : 'bg-rose-400/20 text-rose-400'
                  }`}>
                    {plPercent >= 0 ? '+' : ''}{plPercent.toFixed(2)}%
                  </div>
                )}
              </div>
            </div>

            {/* Margin info (collapsible on mobile) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[10px] sm:text-xs">
              <div className="bg-white/5 rounded px-2 py-1.5">
                <div className="text-white/50">Margin</div>
                <div className="text-white/80 font-medium truncate">
                  {formatCurrency(accountData.margin, currency)}
                </div>
              </div>
              <div className="bg-white/5 rounded px-2 py-1.5">
                <div className="text-white/50">Free Margin</div>
                <div className="text-white/80 font-medium truncate">
                  {formatCurrency(accountData.freeMargin, currency)}
                </div>
              </div>
              <div className="bg-white/5 rounded px-2 py-1.5 col-span-2 sm:col-span-1">
                <div className="text-white/50">Margin Level</div>
                <div className={`font-medium ${
                  accountData.marginLevel == null ? 'text-white/80' :
                  accountData.marginLevel > 200 ? 'text-emerald-400' :
                  accountData.marginLevel > 100 ? 'text-yellow-400' : 'text-rose-400'
                }`}>
                  {formatPercent(accountData.marginLevel)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && !accountData.balance && !error && (
          <div className="text-xs text-gray-400 text-center py-4">
            Select an account to view balance
          </div>
        )}
      </CardContent>
    </Card>
  );
}
